import styled from '@emotion/styled'

import { useEffect, useState } from 'react'

import { LIST_SIZE } from '@/blocks/constants'
import { builderLightText } from '@/blocks/utils'
import { Button } from '@/components/Button'
import {
  Container,
  type BackgroundColor,
  type ContainerProps,
  type Space,
} from '@/components/Container'
import type { HasCountdownProps } from '@/components/Countdown/types'
import { Media, type MediaProps } from '@/components/Media'
import {
  Display,
  HeadingL,
  Typography,
  type TypographyColorModifier,
} from '@/components/Typography'
import { decodeURL } from '@/core/utils/dom/decodeURL'
import type { BlockButtonProps, TitleProps } from '@/models'

import { CONFIG } from '@/styles/emotion.config'

import { useConsultData } from '../hooks/useConsultData'
import type { ConsultData } from '../types'

const HeroProductPremiumBlockStyles = styled.div({
  '& .e': {
    margin: '0',
    padding: '0',
    width: '100%',
  },

  /* container */
  '& .a': {
    display: 'grid',
    gridTemplateColumns: '1fr 25rem',
    gridTemplateAreas: '"title image" "button image"',
    marginTop: '1.5rem',
    alignItems: 'start',
    paddingTop: '3rem',
  },

  /* text container */
  '& .b': {
    gridArea: 'title',
    alignSelf: 'flex-start',
  },

  /* title container */
  '& .t': {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '3rem',
    marginBottom: '1.5rem',
    lineHeight: '3.25rem',
  },

  /* subtitle container */
  '& .s': {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '1.5rem',
    lineHeight: '1.75rem',
    paddingRight: '1rem',
    marginBottom: '1rem',
  },

  /* button */
  '& .c': {
    gridArea: 'button',
    width: '20rem',
    marginTop: '-4rem',
  },

  '& .bp': {
    gridArea: 'button',
    width: '20rem',
    marginBottom: '1.5rem',
  },

  /* media container */
  '& .d': {
    position: 'relative',
    width: '90%',
    maxWidth: '25rem',
    gridArea: 'image',
  },

  '& .d img': {
    width: '100%',
    height: '100%',
  },

  [CONFIG.media.mobileLg]: {
    /* container */
    '.a': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      alignItems: 'flex-start',
      justifyContent: 'center',
      margin: '0 auto',
      gridTemplateAreas: '"title" "image" "button"',
    },

    /* title container */
    '& .t': {
      fontSize: '1.5rem',
      marginBottom: '1rem',
      lineHeight: '1.75rem',
    },

    /* subtitle container */
    '& .s': {
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },

    '& .x': {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },

    /* media container */
    '.d': {
      margin: '0 auto 1rem',
      maxWidth: '12rem',
      height: 'auto',
    },

    '.c': {
      width: '100%',
      maxWidth: '20rem',
      margin: '0 auto',
      marginTop: '-1rem',
    },
  },
})

interface Props extends BlockButtonProps, HasCountdownProps {
  id?: string
  title?: TitleProps
  subtitle?: TitleProps
  subtitleWeight?: 'regular' | 'bold'
  background: BackgroundColor
  size?: 'm' | 'l'
  space?: Space
  backgroundColor?: string
  colorModifier?: TypographyColorModifier
  /**
   * Midia Medusa - Preparação para receber props da migração
   */
  midia: MediaProps
}

const HeroProductPremiumBlock = ({
  id,
  title,
  subtitle,
  subtitleWeight,
  hasButton = false,
  button,
  buttonLabel,
  buttonType,
  buttonValue,
  buttonAriaLabel,
  buttonRounded,
  buttonModifier,
  midia: medusaMidiaProps,
  size = 'l',
  space = 'l',
  background,
  backgroundColor,
  colorModifier,
}: Props) => {
  const TitleComponent = size === LIST_SIZE.lg ? Display : HeadingL
  const lightText = builderLightText(colorModifier)

  const containerProps: ContainerProps = {
    contentClassName: 'a',
    className: 'e',
    spaceOut: space,
    spaceIn: 'm',
    backgroundColor: background,
    backgroundColorHex: backgroundColor,
    id,
  }

  const buttonProps = {
    buttonLabel: buttonLabel || button?.label,
    buttonType: buttonType || button?.action?.type,
    buttonValue: buttonValue || button?.action?.value,
    buttonAriaLabel: buttonAriaLabel || button?.label,
    buttonModifier: buttonModifier || 'solid-invert',
    buttonRounded: buttonRounded,
  }

  const [consultData, setConsultData] = useState<ConsultData>()

  const hookConsultData = useConsultData()

  useEffect(() => {
    hookConsultData.then((consultData) => setConsultData(consultData))
  }, [])

  return (
    <HeroProductPremiumBlockStyles>
      <Container {...containerProps}>
        <div className="b">
          {title && title.text && (
            <TitleComponent dark="high" light={lightText} className="t" tag={title.tag || 'h1'}>
              {consultData?.isPremium ? 'Você que é Serasa Premium tem desconto' : title.text}
            </TitleComponent>
          )}

          {subtitle && subtitle.text && (
            <Typography
              dark="medium"
              light={lightText}
              className="s"
              tag={subtitle.tag}
              emphasys={subtitleWeight === 'bold'}
            >
              {consultData?.isPremium
                ? 'Além de todos os benefícios de ser assinante Premium, você também pode realizar consultas a CPFs e CNPJs com desconto.'
                : subtitle.text}
            </Typography>
          )}
          {subtitle && subtitle.text && consultData?.isPremium && hasButton && buttonProps && (
            <div className="x">
              <Button
                className="bp"
                action="scroll"
                value="primeira-dobra"
                size="lg"
                modifier="solid"
                rounded={true}
                aria-label="Aproveitar meu desconto"
                aria-hidden={hasButton}
              >
                Aproveitar meu desconto
              </Button>
            </div>
          )}
        </div>

        <div className="c">
          {!consultData?.isPremium && hasButton && buttonProps && (
            <Button
              className="e eu-sm-2"
              action={buttonProps.buttonType}
              value={decodeURL(buttonProps.buttonValue)}
              size="lg"
              modifier={buttonProps.buttonModifier}
              rounded={buttonProps.buttonRounded}
              aria-label={buttonProps.buttonAriaLabel}
              aria-hidden={!hasButton}
            >
              {buttonProps.buttonLabel}
            </Button>
          )}
        </div>

        <div className="d">
          <Media width={230} height={310} layout="responsive" priority {...medusaMidiaProps} />
        </div>
      </Container>
    </HeroProductPremiumBlockStyles>
  )
}

export default HeroProductPremiumBlock
